// src/pages/user/Tickets.js
// Tickets de support du client (acheteur)
// Maxula

import React, { useEffect, useState, useRef } from 'react';
import { FiPlus, FiSend, FiArrowLeft, FiLifeBuoy, FiClock, FiMessageSquare, FiX, FiCheckCircle, FiRotateCcw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { ticketAPI } from '../../services/api';

const STATUS = {
  open: { cls: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200', dot: 'bg-blue-500', label: 'Ouvert' },
  pending: { cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200', dot: 'bg-amber-500', label: 'En attente' },
  closed: { cls: 'bg-gray-100 text-gray-500 ring-1 ring-gray-200', dot: 'bg-gray-400', label: 'Fermé' }
};
const statusOf = (s) => STATUS[s] || STATUS.open;

// Date robuste (les messages stockent `at` ; certains objets `createdAt`).
const fmtDateTime = (v) => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (v) => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const s = statusOf(status);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

const CustomerTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', priority: 'normal' });
  const [reply, setReply] = useState('');
  const threadRef = useRef(null);

  const load = async () => {
    try { setLoading(true); const res = await ticketAPI.getMine(); setTickets(res.data?.data || []); }
    catch (e) {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openTicket = async (id) => {
    try { const res = await ticketAPI.getOne(id); setActive(res.data?.data); }
    catch (e) { toast.error('Erreur lors de l\'ouverture du ticket'); }
  };

  const create = async () => {
    if (!form.subject.trim() || !form.message.trim()) return toast.error('Sujet et message requis');
    try {
      await ticketAPI.create(form);
      toast.success('Ticket créé');
      setCreating(false); setForm({ subject: '', message: '', priority: 'normal' });
      load();
    } catch (e) { toast.error('Erreur lors de la création'); }
  };

  const sendReply = async () => {
    if (!reply.trim() || sending) return;
    try {
      setSending(true);
      const res = await ticketAPI.reply(active._id || active.id, reply.trim());
      setActive(res.data?.data); setReply(''); load();
    } catch (e) { toast.error('Erreur lors de l\'envoi'); }
    finally { setSending(false); }
  };

  // Le demandeur peut fermer (résolu) son ticket ou le rouvrir.
  const changeStatus = async (s) => {
    try {
      const res = await ticketAPI.setStatus(active._id || active.id, s);
      setActive(res.data?.data); load();
      toast.success(s === 'closed' ? 'Ticket marqué comme résolu' : 'Ticket rouvert');
    } catch (e) { toast.error('Action impossible'); }
  };

  // auto-scroll en bas du fil quand le ticket / les messages changent
  useEffect(() => {
    if (active && threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [active]);

  if (loading) {
    return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;
  }

  // ---------- Vue détail (conversation) ----------
  if (active) {
    const msgs = Array.isArray(active.messages) ? active.messages : [];
    const isClosed = active.status === 'closed';
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => setActive(null)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 mb-4">
          <FiArrowLeft /> Mes tickets
        </button>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* En-tête ticket */}
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{active.subject}</h2>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                <span className="inline-flex items-center gap-1"><FiClock size={12} /> {fmtDate(active.createdAt) || '—'}</span>
                <span className="inline-flex items-center gap-1"><FiMessageSquare size={12} /> {msgs.length} message{msgs.length > 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StatusBadge status={active.status} />
              {active.status !== 'closed' ? (
                <button onClick={() => changeStatus('closed')} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-50">
                  <FiCheckCircle size={13} /> Marquer comme résolu
                </button>
              ) : (
                <button onClick={() => changeStatus('open')} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-primary-200 text-primary-600 hover:bg-primary-50">
                  <FiRotateCcw size={13} /> Rouvrir
                </button>
              )}
            </div>
          </div>

          {/* Fil de conversation */}
          <div ref={threadRef} className="px-5 py-5 space-y-4 max-h-[26rem] overflow-y-auto bg-gray-50/30">
            {msgs.length === 0 && <div className="text-center text-sm text-gray-400 py-6">Aucun message.</div>}
            {msgs.map((m, i) => {
              const fromSupport = m.senderRole === 'admin';
              const when = fmtDateTime(m.at || m.createdAt);
              return (
                <div key={i} className={`flex items-end gap-2 ${fromSupport ? 'justify-start' : 'justify-end'}`}>
                  {fromSupport && (
                    <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center shrink-0">
                      <FiLifeBuoy size={15} />
                    </div>
                  )}
                  <div className={`max-w-[78%] ${fromSupport ? 'items-start' : 'items-end'} flex flex-col`}>
                    <div className={`px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed shadow-sm ${
                      fromSupport
                        ? 'bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-bl-md'
                        : 'bg-primary-600 text-white rounded-2xl rounded-br-md'
                    }`}>
                      {m.body}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1 px-1">
                      {fromSupport ? 'Support' : 'Vous'}{when ? ` · ${when}` : ''}
                    </div>
                  </div>
                  {!fromSupport && (
                    <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0 text-xs font-bold">
                      Vous
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Zone de réponse */}
          <div className="px-5 py-4 border-t border-gray-100 bg-white">
            {isClosed ? (
              <div className="text-center text-sm text-gray-400 py-1">Ce ticket est fermé.</div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder="Votre réponse…"
                  className="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none"
                />
                <button
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  className="w-11 h-11 rounded-full bg-primary-600 text-white flex items-center justify-center hover:bg-primary-700 disabled:opacity-50 shrink-0"
                  aria-label="Envoyer"
                >
                  <FiSend size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Vue liste ----------
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support</h1>
          <p className="text-sm text-gray-500 mt-0.5">Une question ? Notre équipe vous répond ici.</p>
        </div>
        <button onClick={() => setCreating((v) => !v)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 shadow-sm">
          {creating ? <FiX /> : <FiPlus />} {creating ? 'Annuler' : 'Nouveau ticket'}
        </button>
      </div>

      {creating && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6 space-y-3">
          <input
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            placeholder="Sujet"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none"
          />
          <textarea
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            rows={4}
            placeholder="Décrivez votre problème…"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none resize-none"
          />
          <div className="flex items-center justify-between gap-3">
            <select
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-200 focus:border-primary-400 outline-none"
            >
              <option value="low">Priorité basse</option>
              <option value="normal">Normale</option>
              <option value="high">Haute</option>
            </select>
            <button onClick={create} className="px-5 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 inline-flex items-center gap-2">
              <FiSend size={15} /> Envoyer
            </button>
          </div>
        </div>
      )}

      {tickets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-16 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-3">
            <FiLifeBuoy size={26} />
          </div>
          <p className="text-gray-700 font-semibold">Aucun ticket pour le moment</p>
          <p className="text-sm text-gray-400 mt-1">Besoin d'aide ? Créez un ticket, le support vous répondra rapidement.</p>
          {!creating && (
            <button onClick={() => setCreating(true)} className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700">
              <FiPlus /> Nouveau ticket
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {tickets.map((t) => {
            const count = Array.isArray(t.messages) ? t.messages.length : (t.messages?.length || 0);
            return (
              <button
                key={t._id || t.id}
                onClick={() => openTicket(t._id || t.id)}
                className="group w-full text-left bg-white rounded-2xl border border-gray-200 p-4 hover:border-primary-300 hover:shadow-sm transition-all flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center shrink-0 group-hover:bg-primary-100">
                    <FiMessageSquare size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{t.subject}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {count} message{count > 1 ? 's' : ''}{fmtDate(t.updatedAt || t.createdAt) ? ` · ${fmtDate(t.updatedAt || t.createdAt)}` : ''}
                    </div>
                  </div>
                </div>
                <StatusBadge status={t.status} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerTickets;
